import React, { useMemo, useState, useEffect } from 'react'

import {
	Check,
	AlertCircle,
	Edit,
	Trash2,
	CheckCircle,
	Loader2,
	MoreVertical,
	CreditCard,
	Plus,
} from 'lucide-react'
import { DateTime } from 'luxon'

import amexIcon from '@/assets/images/icons/cards/amex.svg'
import mastercardIcon from '@/assets/images/icons/cards/mastercard.svg'
import visaIcon from '@/assets/images/icons/cards/visa.svg'
import { PaymentToken } from '@/core/api/entities/paymentToken'
import {
	useDeletePaymentToken,
	usePaymentTokens,
} from '@/core/hooks/payment/usePaymentTokens'
import { useUpdatePaymentToken } from '@/core/hooks/payment/useUpdatePaymentToken'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/shared/components/atoms/Dropdown/Dropdown'
import { Skeleton } from '@/shared/components/atoms/Skeleton'
import ConfirmationModal from '@/shared/components/molecules/ConfirmationModal/ConfirmationModal'
import { useNotificationContext } from '@/shared/context/NotificationProvider'
import { extractErrorMessage } from '@/shared/utils/helpers'

import { EditPaymentMethodModal } from './EditPaymentMethodModal'

interface CardPaymentMethod {
	id: string
	type: 'visa' | 'mastercard' | 'amex'
	brand: string
	last4: string
	expMonth: string
	expYear: string
	isDefault: boolean
	isExpired: boolean
}

interface SepaPaymentMethod {
	id: string
	iban: string
	accountHolder: string
	email: string
}

const getCardIcon = (brand: string) => {
	const normalizedBrand = brand.toLowerCase()
	if (normalizedBrand.includes('visa')) {
		return visaIcon
	}
	if (normalizedBrand.includes('mastercard')) {
		return mastercardIcon
	}
	if (
		normalizedBrand.includes('amex') ||
		normalizedBrand.includes('american express')
	) {
		return amexIcon
	}
	return visaIcon
}

const getCardName = (brand: string) => {
	const normalizedBrand = brand.toLowerCase()
	if (normalizedBrand.includes('visa')) {
		return 'Visa'
	}
	if (normalizedBrand.includes('mastercard')) {
		return 'Mastercard'
	}
	if (
		normalizedBrand.includes('amex') ||
		normalizedBrand.includes('american express')
	) {
		return 'American Express'
	}
	return brand
}

const normalizeCardBrand = (brand: string): 'visa' | 'mastercard' | 'amex' => {
	const normalizedBrand = brand.toLowerCase()
	if (normalizedBrand.includes('visa')) {
		return 'visa'
	}
	if (normalizedBrand.includes('mastercard')) {
		return 'mastercard'
	}
	if (
		normalizedBrand.includes('amex') ||
		normalizedBrand.includes('american express')
	) {
		return 'amex'
	}
	return 'visa'
}

const isCardExpired = (
	expMonth: number,
	expYear: number,
	expiresAt?: DateTime | null
): boolean => {
	// Use expires_at if available, otherwise calculate from expMonth/expYear
	if (expiresAt) {
		return DateTime.now() > expiresAt
	}
	const now = DateTime.now()
	const expirationDate = DateTime.fromObject({
		month: expMonth,
		year: expYear,
	}).endOf('month')
	return now > expirationDate
}

const formatIBAN = (iban: string): string => {
	if (!iban || iban.startsWith('****')) return iban
	// Format IBAN with spaces every 4 characters
	return iban.replace(/(.{4})/g, '$1 ').trim()
}

interface PaymentMethodsListProps {
	onAddPaymentMethod: () => void
	hasSEPA: boolean
	showMobileEmptyState?: boolean
	onHasPaymentMethodsChange?: (hasPaymentMethods: boolean) => void
}

export const PaymentMethodsList: React.FC<PaymentMethodsListProps> = ({
	onAddPaymentMethod,
	hasSEPA,
	showMobileEmptyState = false,
	onHasPaymentMethodsChange,
}) => {
	const { t } = useTranslation()
	const { data: paymentTokens, isLoading } = usePaymentTokens({
		config: useAccountConfig({}),
	})
	const { showNotification } = useNotificationContext()
	const [updatingTokenId, setUpdatingTokenId] = useState<number | null>(null)
	const [deletingTokenId, setDeletingTokenId] = useState<number | null>(null)
	const [editingToken, setEditingToken] = useState<PaymentToken | null>(null)
	const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
	const [tokenIdToDelete, setTokenIdToDelete] = useState<number | null>(null)

	const updatePaymentTokenMutation = useUpdatePaymentToken({
		options: {
			onSuccess: () => {
				// Clear loading state immediately
				setUpdatingTokenId(null)
				showNotification({
					type: 'success',
					message:
						t('payment_method.set_default_success') ||
						'Payment method set as default successfully',
				})
			},
			onError: (error: any) => {
				// Clear loading state immediately
				setUpdatingTokenId(null)
				showNotification({
					type: 'error',
					message: t('payment_method.set_default_error') || 'Error',
					description: extractErrorMessage(error, t('common.try_again_later')),
				})
			},
			onSettled: () => {
				// Ensure loading state is cleared as fallback
				setUpdatingTokenId(null)
			},
		},
	})

	const deletePaymentTokenMutation = useDeletePaymentToken({
		config: useAccountConfig({}),
		options: {
			onSuccess: () => {
				showNotification({
					type: 'success',
					message:
						t('payment_method.delete_success') ||
						'Payment method removed successfully',
				})
				// Clear loading state immediately
				setDeletingTokenId(null)
			},
			onError: (error: any) => {
				showNotification({
					type: 'error',
					message: t('payment_method.delete_error') || 'Error',
					description: extractErrorMessage(error, t('common.try_again_later')),
				})
				// Clear loading state immediately
				setDeletingTokenId(null)
			},
			onSettled: () => {
				// Ensure loading state is cleared as fallback
				setDeletingTokenId(null)
			},
		},
	})

	const handleMakeDefault = (paymentTokenId: number) => {
		setUpdatingTokenId(paymentTokenId)
		updatePaymentTokenMutation.mutate({
			paymentTokenId,
			data: { is_default: true },
		})
	}

	const handleRemove = (paymentTokenId: number) => {
		setTokenIdToDelete(paymentTokenId)
		setShowDeleteConfirmation(true)
	}

	const handleConfirmRemove = () => {
		if (tokenIdToDelete !== null) {
			setDeletingTokenId(tokenIdToDelete)
			deletePaymentTokenMutation.mutate(tokenIdToDelete)
			setShowDeleteConfirmation(false)
			setTokenIdToDelete(null)
		}
	}

	const handleCloseDeleteModal = () => {
		setShowDeleteConfirmation(false)
		setTokenIdToDelete(null)
	}

	const handleEdit = (paymentTokenId: number) => {
		const token = paymentTokens?.find((t) => t.id === paymentTokenId)
		if (token) {
			setEditingToken(token)
		}
	}

	const handleCloseEditModal = () => {
		setEditingToken(null)
	}

	// Clear loading state when mutations are no longer pending
	useEffect(() => {
		if (!updatePaymentTokenMutation.isPending && updatingTokenId !== null) {
			setUpdatingTokenId(null)
		}
		if (!deletePaymentTokenMutation.isPending && deletingTokenId !== null) {
			setDeletingTokenId(null)
		}
	}, [
		updatePaymentTokenMutation.isPending,
		deletePaymentTokenMutation.isPending,
		updatingTokenId,
		deletingTokenId,
	])

	const isUpdating = updatingTokenId !== null || deletingTokenId !== null

	const cards = useMemo<CardPaymentMethod[]>(() => {
		if (!paymentTokens) return []
		return paymentTokens
			.filter((token) => token.type === 'card' && token.paymentInfo?.card)
			.map((token) => {
				const cardInfo = token.paymentInfo!.card!
				const expMonth = cardInfo.expMonth
				const expYear = cardInfo.expYear
				return {
					id: String(token.id),
					type: normalizeCardBrand(cardInfo.brand),
					brand: cardInfo.brand,
					last4: cardInfo.last4,
					expMonth: String(expMonth).padStart(2, '0'),
					expYear: String(expYear).slice(-2),
					isDefault: token.isDefault,
					isExpired: isCardExpired(expMonth, expYear, token.expiresAt),
				}
			})
	}, [paymentTokens])

	const sepaAccounts = useMemo<SepaPaymentMethod[]>(() => {
		if (!paymentTokens) return []
		return paymentTokens
			.filter((token) => token.type === 'sepa' && token.paymentInfo?.sepa)
			.map((token) => {
				const sepaInfo = token.paymentInfo!.sepa!
				return {
					id: String(token.id),
					iban: formatIBAN(sepaInfo.iban),
					accountHolder: sepaInfo.accountHolderName,
					email: sepaInfo.accountHolderEmail,
				}
			})
	}, [paymentTokens])

	const hasPaymentMethods = cards.length > 0 || sepaAccounts.length > 0

	useEffect(() => {
		if (!isLoading) {
			onHasPaymentMethodsChange?.(hasPaymentMethods)
		}
	}, [hasPaymentMethods, isLoading, onHasPaymentMethodsChange])

	if (isLoading) {
		return (
			<>
				<div className="w-full rounded-lg border border-border bg-card dark:bg-muted overflow-hidden">
					{/* Cards Section Header */}
					<div className="px-4 py-3 bg-muted dark:bg-shade border-b border-border">
						<Skeleton className="h-6 w-20" />
					</div>

					{/* Cards Skeleton */}
					<div className="divide-y divide-border">
						{[1, 2, 3].map((i) => (
							<div key={i} className="flex items-start justify-between p-4">
								<div className="flex items-center gap-4 flex-1">
									<Skeleton className="w-10 h-7 rounded" />
									<div className="flex-1 space-y-2">
										<div className="flex items-center justify-between">
											<Skeleton className="h-5 w-32" />
											<div className="flex items-center gap-2 ml-4">
												<Skeleton className="h-6 w-16 rounded-[6px]" />
												<Skeleton className="h-8 w-8 rounded" />
											</div>
										</div>
										<Skeleton className="h-4 w-20" />
									</div>
								</div>
							</div>
						))}
					</div>

					{/* SEPA Section Header */}
					<div className="px-4 py-3 bg-muted dark:bg-shade border-t border-b border-border">
						<Skeleton className="h-6 w-16" />
					</div>

					{/* SEPA Skeleton */}
					<div className="divide-y divide-border">
						{[1].map((i) => (
							<div key={i} className="flex items-center justify-between p-4">
								<div className="flex items-center gap-4 flex-1">
									<Skeleton className="w-10 h-7 rounded" />
									<div className="flex-1 space-y-2">
										<Skeleton className="h-5 w-48" />
										<Skeleton className="h-4 w-40" />
									</div>
								</div>
								<Skeleton className="h-8 w-8 rounded ml-4" />
							</div>
						))}
					</div>
				</div>
			</>
		)
	}

	if (showMobileEmptyState && !hasPaymentMethods) {
		return (
			<div className="flex flex-col items-center justify-center text-center px-4 pb-8 pt-12">
				<div className="w-[88px] h-[88px] rounded-2xl bg-muted flex items-center justify-center">
					<CreditCard className="w-11 h-11 text-muted-foreground" />
				</div>
				<h2 className="mt-8 text-4xl font-semibold text-foreground leading-tight">
					{t('payment_method.no_payment_methods_found')}
				</h2>
				<p className="mt-4 max-w-[320px] text-lg text-muted-foreground">
					{t('payment_method.empty_state_search_description')}
				</p>
				<Button
					onClick={onAddPaymentMethod}
					className="mt-8 h-14 rounded-full px-8 bg-white text-black hover:bg-white/90"
				>
					<Plus className="w-5 h-5 mr-2" />
					{t('payment_method.add_payment_method_label')}
				</Button>
			</div>
		)
	}

	return (
		<>
			<div className="w-full rounded-lg border border-border bg-card dark:bg-muted overflow-hidden">
				{/* Cards Section Header */}
				<div className="px-4 py-3 bg-muted dark:bg-shade border-b border-border">
					<h2 className="text-lg font-semibold text-foreground">
						{t('payment_method.cards')}
					</h2>
				</div>

				{/* Cards List */}
				{cards.length > 0 ? (
					<div className="divide-y divide-border">
						{cards.map((card) => {
							const isThisCardUpdating = updatingTokenId === Number(card.id)
							const isThisCardDeleting = deletingTokenId === Number(card.id)
							const isThisCardProcessing =
								isThisCardUpdating || isThisCardDeleting

							return (
								<div
									key={card.id}
									className={`flex items-start justify-between p-4 transition-colors relative ${
										isThisCardProcessing
											? 'opacity-60 pointer-events-none'
											: 'hover:bg-muted/50 dark:hover:bg-shade'
									}`}
								>
									{isThisCardProcessing && (
										<div className="absolute inset-0 flex items-center justify-center bg-background/50 dark:bg-background/50 z-10 rounded">
											<div className="flex items-center gap-2 px-4 py-2 bg-card dark:bg-muted border border-border rounded-lg shadow-lg">
												<Loader2 className="h-4 w-4 animate-spin text-primary" />
												<span className="text-sm font-medium text-foreground">
													{isThisCardDeleting
														? t('payment_method.removing_payment_method')
														: t('payment_method.setting_as_default')}
												</span>
											</div>
										</div>
									)}
									<div className="flex items-center gap-4 flex-1">
										<img
											src={getCardIcon(card.brand)}
											alt={getCardName(card.brand)}
											className="w-10 h-7 object-contain"
										/>
										<div className="flex-1">
											<div className="flex items-center justify-between">
												<span className="text-base font-medium text-foreground">
													{getCardName(card.brand)} {card.last4}
												</span>
												<div className="flex items-center gap-2 ml-4">
													{card.isDefault && (
														<div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-zone-online-bg border border-zone-online/20 text-zone-online">
															<div className="w-4 h-4 rounded-full bg-zone-online flex items-center justify-center flex-shrink-0">
																<Check
																	className="h-2.5 w-2.5 text-accent-foreground"
																	strokeWidth={3}
																/>
															</div>
															<span className="text-xs font-semibold">
																{t('payment_method.default')}
															</span>
														</div>
													)}
													{card.isExpired && (
														<div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[6px] border text-muted-foreground border-muted-foreground/30">
															<AlertCircle className="h-3 w-3" />
															<span className="text-xs font-semibold">
																{t('payment_method.expired')}
															</span>
														</div>
													)}
													<DropdownMenu>
														<DropdownMenuTrigger asChild>
															<Button
																variant="ghost"
																size="icon"
																className="h-8 w-8"
																disabled={isUpdating}
															>
																<MoreVertical className="h-4 w-4" />
															</Button>
														</DropdownMenuTrigger>
														<DropdownMenuContent align="end">
															<DropdownMenuItem
																onClick={() => handleEdit(Number(card.id))}
																disabled={isUpdating}
															>
																<Edit className="mr-2 h-4 w-4" />
																{t('actions.edit')}
															</DropdownMenuItem>
															<DropdownMenuItem
																onClick={() =>
																	handleMakeDefault(Number(card.id))
																}
																disabled={card.isDefault || isUpdating}
															>
																{isThisCardUpdating ? (
																	<Loader2 className="mr-2 h-4 w-4 animate-spin" />
																) : (
																	<CheckCircle className="mr-2 h-4 w-4" />
																)}
																{isThisCardUpdating
																	? t('payment_method.setting_as_default')
																	: t('payment_method.make_default')}
															</DropdownMenuItem>
															<DropdownMenuItem
																onClick={() => handleRemove(Number(card.id))}
																disabled={isUpdating}
																variant="destructive"
															>
																{isThisCardDeleting ? (
																	<Loader2 className="mr-2 h-4 w-4 animate-spin" />
																) : (
																	<Trash2 className="mr-2 h-4 w-4" />
																)}
																{isThisCardDeleting
																	? t('payment_method.removing')
																	: t('actions.remove')}
															</DropdownMenuItem>
														</DropdownMenuContent>
													</DropdownMenu>
												</div>
											</div>
											<p className="text-sm text-muted-foreground mt-1">
												{t('payment_method.expiration')} {card.expMonth}/
												{card.expYear}
											</p>
										</div>
									</div>
								</div>
							)
						})}
					</div>
				) : (
					<div className="p-8 text-center text-muted-foreground">
						<p className="text-sm">{t('payment_method.no_cards_found')}</p>
					</div>
				)}

				{(hasSEPA || sepaAccounts.length > 0) && (
					<>
						{/* SEPA Section Header */}
						<div className="px-4 py-3 bg-muted dark:bg-shade border-t border-b border-border">
							<h2 className="text-lg font-semibold text-foreground">
								{t('payment_method.sepa')}
							</h2>
						</div>

						{/* SEPA List */}
						{sepaAccounts.length > 0 ? (
							<div className="divide-y divide-border">
								{sepaAccounts.map((sepa) => {
									const isThisSepaDeleting = deletingTokenId === Number(sepa.id)

									return (
										<div
											key={sepa.id}
											className={`flex items-center justify-between p-4 transition-colors relative ${
												isThisSepaDeleting
													? 'opacity-60 pointer-events-none'
													: 'hover:bg-muted/50 dark:hover:bg-shade'
											}`}
										>
											{isThisSepaDeleting && (
												<div className="absolute inset-0 flex items-center justify-center bg-background/50 dark:bg-background/50 z-10 rounded">
													<div className="flex items-center gap-2 px-4 py-2 bg-card dark:bg-muted border border-border rounded-lg shadow-lg">
														<Loader2 className="h-4 w-4 animate-spin text-primary" />
														<span className="text-sm font-medium text-foreground">
															{t('payment_method.removing_payment_method')}
														</span>
													</div>
												</div>
											)}
											<div className="flex items-center gap-4 flex-1">
												<div className="w-10 h-7 rounded bg-blue-600 flex items-center justify-center">
													<span className="text-white text-xs font-semibold">
														{t('payment_method.sepa')}
													</span>
												</div>
												<div className="flex-1">
													<p className="text-base font-medium text-foreground">
														{sepa.iban}
													</p>
													<p className="text-sm text-muted-foreground mt-1">
														{sepa.accountHolder
															? `${sepa.accountHolder} • ${sepa.email}`
															: sepa.email}
													</p>
												</div>
											</div>
											<div className="ml-4">
												<DropdownMenu>
													<DropdownMenuTrigger asChild>
														<Button
															variant="ghost"
															size="icon"
															className="h-8 w-8"
															disabled={isUpdating}
														>
															<MoreVertical className="h-4 w-4" />
														</Button>
													</DropdownMenuTrigger>
													<DropdownMenuContent align="end">
														<DropdownMenuItem
															onClick={() => handleEdit(Number(sepa.id))}
															disabled={isUpdating}
														>
															<Edit className="mr-2 h-4 w-4" />
															{t('actions.edit')}
														</DropdownMenuItem>
														<DropdownMenuItem
															onClick={() => handleRemove(Number(sepa.id))}
															disabled={isUpdating}
															variant="destructive"
														>
															{isThisSepaDeleting ? (
																<Loader2 className="mr-2 h-4 w-4 animate-spin" />
															) : (
																<Trash2 className="mr-2 h-4 w-4" />
															)}
															{isThisSepaDeleting
																? t('payment_method.removing')
																: t('actions.remove')}
														</DropdownMenuItem>
													</DropdownMenuContent>
												</DropdownMenu>
											</div>
										</div>
									)
								})}
							</div>
						) : (
							<div className="p-8 text-center text-muted-foreground">
								<p className="text-sm">
									{t('payment_method.no_sepa_accounts_found')}
								</p>
							</div>
						)}
					</>
				)}

				{/* Edit Payment Method Modal */}
				<EditPaymentMethodModal
					isOpen={editingToken !== null}
					onClose={handleCloseEditModal}
					paymentToken={editingToken}
				/>

				{/* Delete Confirmation Modal */}
				<ConfirmationModal
					visible={showDeleteConfirmation}
					title={t('payment_method.delete_modal.title')}
					description={t('payment_method.delete_modal.description')}
					onConfirm={handleConfirmRemove}
					onClose={handleCloseDeleteModal}
					confirmText={t('actions.delete')}
					cancelText={t('actions.cancel')}
					variant="destructive"
				/>
			</div>
		</>
	)
}
